import i18next from "i18next";
import { create } from "zustand";
import {
  type UserFormSummary,
  type WorkflowRunInput,
  type WorkflowVersion,
} from "#app/components/workflow/activity-timeline";
import { useSessionsStore } from "#app/store/sessions";
import { track } from "#app/utils/amplitude.client";
import {
  MODEL_ACCEPTED_EXTENSIONS,
  MODEL_ACCEPTED_MIME_TYPES,
} from "#app/utils/attachment-types";
import { authFetch } from "#app/utils/auth.client.tsx";
import {
  getChatLocale,
  appendChatLocaleToFormData,
} from "#app/utils/chat-notification-i18n";
import { streamChat } from "#app/utils/chat-sse";
import { message } from "#app/utils/message";
import { countTokensForMessage } from "#app/utils/token-counter.client";
import { loadUserTokenStats } from "#app/utils/token-stats.client";
import { type PlannerWorkflow } from "#app/utils/types";
import {
  getChatMessages,
  getChatRestorePoints,
  getWorkflowServiceContextHeaders,
  cancelWorkflowRuns,
  deleteContextFile,
  restoreChatToMessage,
} from "#app/utils/workflow-svc";
import { useGlobalStore } from "./global";

/**
 * The id of the pseudo-to-do the stream timer keeps on the streaming assistant
 * message to carry elapsed time. It is NOT a step: it stays `started` for the
 * life of the stream, the header renders it as a badge, and both the list and
 * the count filter it out.
 *
 * Defined here rather than in `#app/utils/stream-timer` (its natural home)
 * because that module imports this store — owning the constant there would make
 * the cycle load-bearing. Previously the literal was written out in four
 * places, which is how the sequential-completion pass below nearly swept it up
 * with the real steps.
 */
export const STREAM_TIMER_TODO_ID = "__stream_timer__";

// Normalize notification/todo messages to a stable key for updates
const normalizeNotificationText = (input: string): string => {
  let text = (input ?? "").toString().trim();
  // Normalize dashes to hyphen for consistency
  text = text.replace(/[–—]/g, "-");
  // Remove leading status tags or words, e.g. "[Completed] ", "Completed: ", "started - ", "Finished - "
  text = text.replace(
    /^\[?\s*(completed|complete|done|failed|error|started|starting|in\s*progress|running|finish(?:ed)?|success(?:ful)?|succeed(?:ed)?|ok)\s*\]?\s*[:\-]?\s*/i,
    "",
  );
  // Remove trailing status decorations, e.g. " - completed", "(failed)", "... done", " - finished"
  text = text.replace(
    /\s*[\(\[\-:,]*\s*(completed|complete|done|failed|error|started|starting|in\s*progress|running|finish(?:ed)?|success(?:ful)?|succeed(?:ed)?|ok)\s*[\)\]\.]*$/i,
    "",
  );
  // Remove trailing progress indicators like "(10%)", "10%", "1/3", "(1/3)", "- step 2 of 5"
  text = text
    .replace(/\s*(?:\(|\[)?\s*\d+(?:\s*\/\s*\d+)?\s*%?\s*(?:\)|\])?\s*$/i, "")
    .replace(/\s*-?\s*step\s*\d+\s*(?:of|\/)\s*\d+\s*$/i, "");
  // Collapse inner whitespace and remove trailing ellipsis
  text = text
    .replace(/\s+/g, " ")
    .replace(/\.{3,}$/, "")
    .trim();
  return text;
};

type ChatMessage = {
  id: string;
  content: string;
  role: "user" | "assistant" | "system";
  timestamp: string;
  isStreaming?: boolean;
  error?: string;
  extra?: Record<string, any>;
  tokenCount?: {
    tokens: number;
    words: number;
    characters: number;
    model: string;
  };
};

// A persisted assistant message whose content is JSON tagged with `__widget__`
// re-hydrates into an interactive/collapsed widget (e.g. attach-trigger) instead
// of rendering as raw text. Returns the widget descriptor or null for plain
// messages. Kept in sync with the backend planner persistence.
export function parseWidgetMessageContent(
  content: string | null | undefined,
): { type: string; data: Record<string, unknown> } | null {
  if (!content || content[0] !== "{") return null;
  try {
    const parsed = JSON.parse(content) as {
      __widget__?: unknown;
      [k: string]: unknown;
    } | null;
    if (parsed?.__widget__) {
      const { __widget__, ...data } = parsed;
      return { type: String(__widget__), data };
    }
  } catch {
    // not JSON — a normal text message
  }
  return null;
}

function normalizeApiChatMessages(
  raw: Array<{
    id: string;
    createdAt: string;
    role: string;
    type: string;
    content: string;
  }>,
): ChatMessage[] {
  const sorted = raw.toSorted(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  return sorted
    .filter((m) => {
      const role = (m.role || "user").toLowerCase();
      // Keep widget messages (their JSON content is non-empty anyway).
      if (parseWidgetMessageContent(m.content)) return true;
      if (role === "assistant" && (!m.content || m.content.trim() === "")) {
        return false;
      }
      return true;
    })
    .map((m) => {
      const widget = parseWidgetMessageContent(m.content);
      const role = ((): "assistant" | "user" | "system" => {
        const rl = (m.role || "user").toLowerCase();
        return rl === "assistant" || rl === "user" || rl === "system"
          ? rl
          : "user";
      })();
      return {
        id: m.id,
        content: widget ? "" : (m.content ?? ""),
        role,
        timestamp: new Date(m.createdAt).toISOString(),
        ...(widget ? { extra: { widget } } : {}),
      };
    });
}

type ChatInputState = {
  value: string;
  isComposing: boolean;
  isSubmitting: boolean;
  placeholder: string;
};

type StreamEvent = {
  type: string;
  payload?: any;
  at: number;
};

/**
 * A message the user submitted while a stream was in flight, held until the
 * stream closes.
 *
 * Deliberately NOT a `ChatMessage` in `messages`: a queued message has not been
 * sent, is not part of the thread, and must not survive a Stop as if it were.
 * It renders as a chip above the composer instead, where it can be edited,
 * removed, or discarded wholesale.
 */
type QueuedMessage = {
  id: string;
  /**
   * Thread this was typed into. The app streams several threads at once
   * (`runningSessionIds`), so the queue is filtered by thread everywhere —
   * switching away hides these chips and switching back restores them, rather
   * than draining them into whatever conversation happens to be open.
   */
  threadId: string | null;
  content: string;
  attachments: Attachment[];
  /**
   * Set when the user stops the stream this was waiting on. The message is kept
   * (they typed it; stopping is not a retraction) but no longer auto-drains —
   * its chip grows Send now / Discard until the user decides. Without this, an
   * abandoned message fires on the next unrelated stream close.
   *
   * Per-message rather than a store-wide flag so that queueing something new
   * after a stop sends only the new message, leaving the abandoned ones parked.
   */
  paused: boolean;
  createdAt: string;
};

/**
 * One row of the live activity trail. Shared by the state field and the
 * `setActivityRunStatus` setter, which used to declare the shape a second time
 * by hand and drifted out of sync with it.
 */
export type ActivityRunStatusItem = {
  stepId: string;
  /**
   * Human-readable step name when the producer supplies it (e.g. the trigger
   * test trail, which has no planned-workflow map to resolve names from).
   * Optional — the main chat run leaves it unset and DesignTimeLogs resolves
   * the name from `plannedWorkflow` instead.
   */
  stepName?: string;
  activityRunId?: string;
  // "cancelled" is written by `stopStream`, which flips in-flight rows when
  // the user stops a run.
  status:
    | "running"
    | "completed"
    | "failed"
    | "retry"
    | "waiting"
    | "cancelled"
    // Terminal: the run stopped waiting for a person on this step.
    | "timeout";
  attempt?: number | null;
  /**
   * Distinguishes a normal step execution row from a condition evaluation
   * row. Condition rows share the parent step's `stepId` but carry a
   * distinct `conditionId`, so they must be tracked as separate entries.
   */
  kind?: "step" | "condition";
  conditionId?: string;
  conditionName?: string;
  outputs?: {
    stdout?: string;
    stderr?: string;
    // The workflow service sanitizer renames stdout/stderr to output/error on
    // the wire. Accept both spellings so the timeline UI can render the
    // collapsed Execution Logs / Error Details panels regardless of source.
    output?: string;
    error?: string;
    displayData?: Record<string, any>;
    executeResult?: Record<string, any>;
    conditionMet?: boolean;
    /** Per-member pass/fail for a rule_chain step (P100). */
    ruleChain?: unknown;
    /** Structured failure data for the debug panel (when auto-retry is off) */
    failureInfo?: {
      error: string;
      failingCode?: string | null;
      stepDescription?: string | null;
      agentPrompt?: string | null;
      attemptCount?: number;
      failureType?: string;
      errorCategory?: string;
      diagnosis?: string | null;
      suggestedApproach?: string | null;
    };
  };
  outputObjectKeys?: string[];
  error?: string | null;
  startedAt?: string | null;
  updatedAt?: string | null;
  /**
   * Human-review submission paired with this activity run. Populated by
   * the activity-run SSE event once the reviewer approves/rejects the
   * form, so the live creation-time audit timeline can render the
   * reviewer's decision and submitted values without reloading the run.
   */
  userForm?: UserFormSummary;
};

type ChatState = {
  messages: ChatMessage[];
  /** Pending sends across all threads, oldest first. Drained one per stream close, FIFO. */
  queuedMessages: QueuedMessage[];
  input: ChatInputState;
  isTyping: boolean;
  isLoading: boolean;
  isStreaming: boolean;
  error: string | null;
  currentSessionId: string | null;
  redirectToThreadId: string | null;
  /**
   * The thread a restore wants re-fetched, set only by `restoreChatTo`. A chat
   * started on this page is addressed with `history.pushState`, which the
   * router never sees, so the route param stays empty and the thread loader
   * has nothing else to key off. Cleared by `resetChat`.
   */
  reloadThreadId: string | null;
  currentSessionTitle: string | null;
  tokenCount: number;
  maxTokens: number;
  attachments: Attachment[];
  contextFiles: Array<{
    id: string;
    name: string;
    objectKey: string;
    contentType?: string;
    sizeBytes?: number;
    createdAt: string;
  }>;
  plannedWorkflow: PlannerWorkflow | null;
  /** Assistant message ids this thread can be restored to or forked from. */
  restorePointMessageIds: string[];
  /** Bumped by a restore so the thread route reloads its messages and graph. */
  threadReloadToken: number;
  workflowRunStatus:
    | "running"
    | "failed"
    | "completed"
    | "cancelled"
    | "waiting"
    | "queued"
    | "timeout"
    | null;
  currentWorkflowRunId: string | null;
  /**
   * Inputs the current workflow run was started with. Surfaced in the
   * Activity Timeline's "Inputs" panel during creator-phase design-time
   * execution. Reset whenever a new run begins.
   */
  runInputs: WorkflowRunInput[];
  formMode: "design" | "execution" | null;
  activityRunStatus: ActivityRunStatusItem[];
  isDesignTimeExecuting: boolean;
  isWaitingForInput: boolean;
  currentInterrupt: any | null;
  interruptsQueue: any[];
  // Thinking display for streaming status
  currentThinking: string | null;
  // Token tracking
  sessionTokenCount: number;
  monthlyTokenCount: number;
  lastMessageTokens: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    model: string;
  } | null;
  lastStreamEvent: StreamEvent | null;
  /** Set when SSE sends unconnected-integrations; opens integration modal until cleared */
  openUnconnectedIntegrationsModal: {
    content: Array<{ slug: string; displayName: string }>;
  } | null;
  /** Cursor pagination for GET /chats/:id/messages (older = scroll up) */
  messagesPagination: {
    hasMore: boolean;
    oldestCursor: string | null;
    newestCursor: string | null;
  } | null;
  isLoadingOlderMessages: boolean;
  currentWorkflowVersion: WorkflowVersion | null;

  // Try Mode properties
  isTryMode: boolean;
  remainingTries: number | null;
  onOnboardRequest: (() => void) | null;
};

type ChatStore = ChatState & {
  updateChatStore: (
    partial: Partial<ChatState> | ((prev: ChatState) => Partial<ChatState>),
  ) => void;
  addMessage: (message: Omit<ChatMessage, "id" | "timestamp">) => void;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  removeMessage: (id: string) => void;
  clearMessages: () => void;
  /** Queue a send for after the current stream. Used by widgets that submit on the user's behalf. */
  enqueueMessage: (content: string, attachments?: Attachment[]) => void;
  /** Drop one chip (its × button). */
  removeQueuedMessage: (id: string) => void;
  /** Drop one chip and move its content + attachments into the composer for editing. */
  editQueuedMessage: (id: string) => void;
  /** "Send now": un-pause this thread's messages and start draining. */
  resumeQueuedMessages: () => void;
  /** "Discard": drop this thread's queued messages, releasing attachment previews. */
  discardQueuedMessages: () => void;
  /**
   * Send this thread's oldest un-paused queued message. Only one per call:
   * sending it opens a new stream whose close drains the next, so a backlog
   * still goes out FIFO.
   */
  drainNextQueuedMessage: () => void;
  setInputValue: (value: string) => void;
  setInputComposing: (isComposing: boolean) => void;
  setInputSubmitting: (isSubmitting: boolean) => void;
  setInputPlaceholder: (placeholder: string) => void;
  updateLastAssistantMessage: (textChunk: string, done?: boolean) => void;
  ensureThreadId: () => Promise<string>;
  sendMessage: (content: string) => void;
  addAttachments: (files: FileList | File[]) => void;
  removeAttachment: (id: string) => void;
  clearAttachments: () => void;
  appendWidgetMessage: (widgetData: any) => void;
  resetState: () => void;
  resetChat: () => void;
  setPlannedWorkflow: (wf: PlannerWorkflow | null) => void;
  /** Swap the locally minted ids of the newest turn for the persisted ones. */
  adoptSavedMessageIds: (ids: {
    messageId: string;
    userMessageId?: string;
  }) => void;
  loadRestorePoints: () => Promise<void>;
  restoreChatTo: (
    messageId: string,
    options?: {
      confirmPublishedRollback?: boolean;
      expectedPublishedVersionId?: string | null;
    },
  ) => Promise<void>;
  setWorkflowRunStatus: (
    status:
      | "running"
      | "failed"
      | "completed"
      | "cancelled"
      | "waiting"
      | "queued"
      | "timeout",
    runId?: string | null,
  ) => void;
  setRunInputs: (inputs: WorkflowRunInput[]) => void;
  runningSessionIds: string[];
  addRunningSessionId: (id: string) => void;
  removeRunningSessionId: (id: string) => void;
  setActivityRunStatus: (items: ActivityRunStatusItem[]) => void;
  setIsDesignTimeExecuting: (isExecuting: boolean) => void;
  // Thinking display helper
  setCurrentThinking: (value: string | null) => void;
  // Per-assistant-message to-dos
  addOrUpdateTodoOnLastAssistantMessage: (
    text: string,
    status?: "started" | "completed" | "failed",
    notificationId?: string,
  ) => void;
  completeTodosOnLastAssistantMessage: (isSuccess: boolean) => void;
  // Token tracking methods
  updateTokenCount: (
    inputTokens: number,
    outputTokens: number,
    model?: string,
  ) => void;
  resetSessionTokens: () => void;
  setMonthlyTokenCount: (count: number) => void;
  initializeTokenStats: () => Promise<void>;
  setLastStreamEvent: (event: Omit<StreamEvent, "at">) => void;
  setOpenUnconnectedIntegrationsModal: (
    payload: {
      content: Array<{ slug: string; displayName: string }>;
    } | null,
  ) => void;
  loadOlderChatMessages: () => Promise<void>;
  stopStream: () => void;
  removeContextFile: (fileId: string) => void;
};

type Attachment = {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  previewUrl?: string;
};

export const useChatStore = create<ChatStore>()((set, get) => ({
  messages: [],
  queuedMessages: [],
  input: {
    value: "",
    isComposing: false,
    isSubmitting: false,
    placeholder: i18next.t("attachments.placeholder", { ns: "chat" }),
  },
  isTyping: false,
  isLoading: false,
  isStreaming: false,
  error: null,
  currentSessionId: null,
  redirectToThreadId: null,
  reloadThreadId: null,
  currentSessionTitle: null,
  formMode: null,
  tokenCount: 0,
  maxTokens: 5000,
  attachments: [],
  contextFiles: [],
  // Thinking display default
  currentThinking: null,
  // Token tracking state
  sessionTokenCount: 0,
  monthlyTokenCount: 0,
  lastMessageTokens: null,
  lastStreamEvent: null,
  openUnconnectedIntegrationsModal: null,
  plannedWorkflow: null,
  restorePointMessageIds: [],
  threadReloadToken: 0,
  runningSessionIds: [],
  workflowRunStatus: "completed",
  currentWorkflowRunId: null,
  runInputs: [],
  activityRunStatus: [],
  isDesignTimeExecuting: false,
  isWaitingForInput: false,
  currentInterrupt: null,
  interruptsQueue: [],
  messagesPagination: null,
  isLoadingOlderMessages: false,
  currentWorkflowVersion: null,
  isTryMode: false,
  remainingTries: null,
  onOnboardRequest: null,

  updateChatStore: (partial) =>
    set((state) => {
      // Hand-maintained mirror of ChatState — it had drifted (isTryMode,
      // remainingTries, onOnboardRequest were never added), so functional
      // updaters could not read them. Completed while adding the queue fields.
      const prev: ChatState = {
        messages: state.messages,
        queuedMessages: state.queuedMessages,
        input: state.input,
        isTyping: state.isTyping,
        isLoading: state.isLoading,
        isStreaming: state.isStreaming,
        error: state.error,
        currentSessionId: state.currentSessionId,
        redirectToThreadId: state.redirectToThreadId,
        reloadThreadId: state.reloadThreadId,
        currentSessionTitle: state.currentSessionTitle,
        formMode: state.formMode,
        tokenCount: state.tokenCount,
        maxTokens: state.maxTokens,
        attachments: state.attachments,
        contextFiles: state.contextFiles,
        plannedWorkflow: state.plannedWorkflow,
        restorePointMessageIds: state.restorePointMessageIds,
        threadReloadToken: state.threadReloadToken,
        workflowRunStatus: state.workflowRunStatus,
        currentWorkflowRunId: state.currentWorkflowRunId,
        runInputs: state.runInputs,
        activityRunStatus: state.activityRunStatus,
        isDesignTimeExecuting: state.isDesignTimeExecuting,
        isWaitingForInput: state.isWaitingForInput,
        currentInterrupt: state.currentInterrupt,
        interruptsQueue: state.interruptsQueue,
        currentThinking: state.currentThinking,
        sessionTokenCount: state.sessionTokenCount,
        monthlyTokenCount: state.monthlyTokenCount,
        lastMessageTokens: state.lastMessageTokens,
        lastStreamEvent: state.lastStreamEvent,
        openUnconnectedIntegrationsModal:
          state.openUnconnectedIntegrationsModal,
        messagesPagination: state.messagesPagination,
        isLoadingOlderMessages: state.isLoadingOlderMessages,
        currentWorkflowVersion: state.currentWorkflowVersion,
        isTryMode: state.isTryMode,
        remainingTries: state.remainingTries,
        onOnboardRequest: state.onOnboardRequest,
      };
      const patch = typeof partial === "function" ? partial(prev) : partial;
      return { ...patch };
    }),

  addMessage: (message) => {
    // Calculate token count for the message
    const tokenCount = countTokensForMessage(
      message.content,
      message.role,
      "gpt-4",
    );

    const newMessage: ChatMessage = {
      ...message,
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      tokenCount,
    };

    set((state) => {
      // Update session token count
      const newSessionTokenCount = state.sessionTokenCount + tokenCount.tokens;

      // Update last message tokens
      const newLastMessageTokens =
        message.role === "user"
          ? {
              inputTokens: tokenCount.tokens,
              outputTokens: 0,
              totalTokens: tokenCount.tokens,
              model: "gpt-4",
            }
          : {
              inputTokens: 0,
              outputTokens: tokenCount.tokens,
              totalTokens: tokenCount.tokens,
              model: "gpt-4",
            };

      return {
        messages: [...state.messages, newMessage],
        sessionTokenCount: newSessionTokenCount,
        lastMessageTokens: newLastMessageTokens,
      };
    });
  },

  updateMessage: (id, updates) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg,
      ),
    }));
  },

  removeMessage: (id) => {
    set((state) => ({
      messages: state.messages.filter((msg) => msg.id !== id),
    }));
  },

  clearMessages: () => {
    set({ messages: [] });
  },

  enqueueMessage: (content, attachments = []) => {
    set((state) => ({
      queuedMessages: [
        ...state.queuedMessages,
        {
          id: `queued-${Date.now()}-${state.queuedMessages.length}`,
          threadId: state.currentSessionId,
          content,
          attachments,
          paused: false,
          createdAt: new Date().toISOString(),
        },
      ],
    }));
  },

  removeQueuedMessage: (id) => {
    set((state) => {
      const target = state.queuedMessages.find((q) => q.id === id);
      for (const a of target?.attachments ?? []) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      }
      return {
        queuedMessages: state.queuedMessages.filter((q) => q.id !== id),
      };
    });
  },

  editQueuedMessage: (id) => {
    const target = get().queuedMessages.find((q) => q.id === id);
    if (!target) return;
    set((state) => {
      // Merge into the composer rather than replacing it — the user may have
      // started typing a fresh draft while the chip sat there. Attachments keep
      // their object URLs; they move to the composer, they are not released.
      const seen = new Set(state.attachments.map((a) => a.id));
      return {
        queuedMessages: state.queuedMessages.filter((q) => q.id !== id),
        input: {
          ...state.input,
          value: [target.content, state.input.value]
            .map((v) => (v ?? "").trim())
            .filter(Boolean)
            .join("\n\n"),
        },
        attachments: [
          ...state.attachments,
          ...target.attachments.filter((a) => {
            if (seen.has(a.id)) return false;
            seen.add(a.id);
            return true;
          }),
        ],
      };
    });
  },

  resumeQueuedMessages: () => {
    const threadId = get().currentSessionId;
    set((state) => ({
      queuedMessages: state.queuedMessages.map((q) =>
        q.threadId === threadId ? { ...q, paused: false } : q,
      ),
    }));
    // Nothing is streaming after a stop, so drain immediately rather than
    // waiting for a stream close that will never come.
    if (!get().isStreaming) get().drainNextQueuedMessage();
  },

  discardQueuedMessages: () => {
    const threadId = get().currentSessionId;
    set((state) => {
      const doomed = (q: QueuedMessage) => q.threadId === threadId;
      for (const q of state.queuedMessages.filter(doomed)) {
        for (const a of q.attachments) {
          if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
        }
      }
      return { queuedMessages: state.queuedMessages.filter((q) => !doomed(q)) };
    });
  },

  drainNextQueuedMessage: () => {
    const threadId = get().currentSessionId;
    const next = get().queuedMessages.find(
      (q) => q.threadId === threadId && !q.paused,
    );
    if (!next) return;
    set((state) => {
      // Merge, don't overwrite — a bare assignment discarded whatever the user
      // had staged in the composer while the stream was running.
      const seen = new Set(state.attachments.map((a) => a.id));
      return {
        queuedMessages: state.queuedMessages.filter((q) => q.id !== next.id),
        attachments: [
          ...state.attachments,
          ...next.attachments.filter((a) => {
            if (seen.has(a.id)) return false;
            seen.add(a.id);
            return true;
          }),
        ],
      };
    });
    get().sendMessage(next.content);
  },

  setInputValue: (value) => {
    set((state) => ({
      input: { ...state.input, value },
    }));
  },

  setInputComposing: (isComposing) => {
    set((state) => ({
      input: { ...state.input, isComposing },
    }));
  },

  setInputSubmitting: (isSubmitting) => {
    set((state) => ({
      input: { ...state.input, isSubmitting },
    }));
  },

  setInputPlaceholder: (placeholder) => {
    set((state) => ({
      input: { ...state.input, placeholder },
    }));
  },

  addAttachments: (files) => {
    // Single source of truth — the same list bounds policy attachments, and both
    // are bounded by what the model can actually read.
    const allowedTypes = new Set<string>(MODEL_ACCEPTED_MIME_TYPES);
    const allowedExtensions = new Set(
      MODEL_ACCEPTED_EXTENSIONS.map((e) => e.replace(/^\./, "")),
    );
    const maxFiles = 5;
    const maxSizeBytes = 10 * 1024 * 1024; // 10MB

    const inputFiles = Array.from(files as FileList);
    const state = get();

    const currentCount = state.attachments.length;
    const remainingSlots = Math.max(0, maxFiles - currentCount);

    if (remainingSlots === 0) {
      message.error(i18next.t("attachments.maxFilesOnly", { ns: "chat" }));
      set({ error: i18next.t("attachments.maxFilesOnly", { ns: "chat" }) });
      return;
    }

    const accepted: Attachment[] = [];
    let rejectedCount = 0;

    for (const file of inputFiles) {
      if (accepted.length >= remainingSlots) {
        message.error(i18next.t("attachments.maxFilesAllowed", { ns: "chat" }));
        rejectedCount++;
        break; // Stop processing further files
      }

      const extension = file.name.split(".").pop()?.toLowerCase();
      if (
        !allowedTypes.has(file.type) &&
        (!extension || !allowedExtensions.has(extension))
      ) {
        message.error(
          i18next.t("attachments.unsupportedType", {
            ns: "chat",
            name: file.name,
          }),
        );
        rejectedCount++;
        continue;
      }

      if (file.size > maxSizeBytes) {
        message.error(
          i18next.t("attachments.exceedsSize", { ns: "chat", name: file.name }),
        );
        rejectedCount++;
        continue;
      }

      const previewUrl = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined;

      accepted.push({
        id: crypto.randomUUID(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        previewUrl,
      });
    }

    if (accepted.length > 0) {
      message.success(
        i18next.t("attachments.addedSuccess", {
          ns: "chat",
          count: accepted.length,
        }),
      );
      set((s) => ({
        attachments: [...s.attachments, ...accepted],
      }));
    } else if (rejectedCount > 0) {
      set({ error: i18next.t("attachments.noneAdded", { ns: "chat" }) });
    } else {
      set({ error: i18next.t("attachments.noneAdded", { ns: "chat" }) });
    }
  },

  removeAttachment: (id) => {
    set((state) => {
      const toRemove = state.attachments.find((a) => a.id === id);
      if (toRemove?.previewUrl) URL.revokeObjectURL(toRemove.previewUrl);
      return {
        attachments: state.attachments.filter((a) => a.id !== id),
      };
    });
  },

  clearAttachments: () => {
    set((state) => {
      for (const a of state.attachments) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      }
      return { attachments: [] };
    });
  },

  resetState: () => {
    set((state) => {
      useGlobalStore.getState().updateSystemStatus({
        showCanvasPanel: false,
        isCanvasFullPage: false,
        showSessionPanel: true,
      });
      for (const a of state.attachments) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      }
      for (const q of state.queuedMessages) {
        for (const a of q.attachments) {
          if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
        }
      }
      return {
        messages: [],
        queuedMessages: [],
        input: {
          value: "",
          isComposing: false,
          isSubmitting: false,
          placeholder: i18next.t("attachments.placeholder", { ns: "chat" }),
        },
        isTyping: false,
        isLoading: false,
        isStreaming: false,
        error: null,
        currentSessionId: null,
        redirectToThreadId: null,
        reloadThreadId: null,
        currentSessionTitle: null,
        formMode: null,
        tokenCount: 0,
        attachments: [],
        contextFiles: [],
        plannedWorkflow: null,
        workflowRunStatus: "completed",
        currentWorkflowRunId: null,
        runInputs: [],
        activityRunStatus: [],
        isDesignTimeExecuting: false,
        isWaitingForInput: false,
        currentInterrupt: null,
        interruptsQueue: [],
        currentThinking: null,
        // Reset token tracking
        sessionTokenCount: 0,
        monthlyTokenCount: 0,
        lastMessageTokens: null,
        lastStreamEvent: null,
        openUnconnectedIntegrationsModal: null,
        messagesPagination: null,
        isLoadingOlderMessages: false,
        currentWorkflowVersion: null,
        isTryMode: false,
        remainingTries: null,
        onOnboardRequest: null,
      };
    });
  },

  loadOlderChatMessages: async () => {
    const state = get();
    const sessionId = state.currentSessionId;
    const pag = state.messagesPagination;
    if (
      !sessionId ||
      !pag?.hasMore ||
      pag.oldestCursor == null ||
      pag.oldestCursor === "" ||
      state.isLoadingOlderMessages
    ) {
      return;
    }
    set({ isLoadingOlderMessages: true });
    try {
      const { messages: raw, pagination } = await getChatMessages(sessionId, {
        before: pag.oldestCursor,
      });
      const normalized = normalizeApiChatMessages(raw);
      set((s) => {
        const existingIds = new Set(s.messages.map((m) => m.id));
        const newOnes = normalized.filter((m) => !existingIds.has(m.id));
        const nextPag =
          pagination != null
            ? {
                hasMore: Boolean(pagination.hasMore),
                oldestCursor: pagination.oldestCursor ?? null,
                newestCursor: pagination.newestCursor ?? null,
              }
            : {
                hasMore: false,
                oldestCursor: pag.oldestCursor,
                newestCursor: pag.newestCursor,
              };
        if (newOnes.length === 0) {
          return {
            messagesPagination: nextPag,
            isLoadingOlderMessages: false,
          };
        }
        const merged = [...newOnes, ...s.messages].sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        );
        return {
          messages: merged,
          messagesPagination: nextPag,
          isLoadingOlderMessages: false,
        };
      });
    } catch (e) {
      console.error("loadOlderChatMessages", e);
      set({ isLoadingOlderMessages: false });
    }
  },

  addRunningSessionId: (id: string) => {
    set((state) => ({
      runningSessionIds: state.runningSessionIds.includes(id)
        ? state.runningSessionIds
        : [...state.runningSessionIds, id],
    }));
  },
  removeRunningSessionId: (id: string) => {
    set((state) => ({
      runningSessionIds: state.runningSessionIds.filter((x) => x !== id),
    }));
  },
  updateLastAssistantMessage: (textChunk: string, done = false) => {
    set((state) => {
      const messagesCopy = [...state.messages];
      let targetIndex = -1;
      for (let i = messagesCopy.length - 1; i >= 0; i--) {
        const msg = messagesCopy[i]!;
        if (msg.role === "assistant" && msg.isStreaming) {
          targetIndex = i;
          break;
        }
      }
      // Design-time `workflow-run` completed used to finalize the bubble while
      // the orchestrator SSE was still open. The next chunk then had nowhere to
      // go (or a notification minted a second empty assistant row), so a short
      // follow-up like "Does this work for you?" appeared as its own bubble.
      // Reopen the last real assistant turn while this thread's stream is alive.
      if (targetIndex < 0 && textChunk.length > 0 && !done) {
        const streamAlive =
          state.isStreaming ||
          (state.currentSessionId != null &&
            state.runningSessionIds.includes(state.currentSessionId));
        if (streamAlive) {
          for (let i = messagesCopy.length - 1; i >= 0; i--) {
            const msg = messagesCopy[i]!;
            if (msg.role !== "assistant") continue;
            if ((msg as any).extra?.widget) continue;
            if ((msg as any).extra?.isNotification) continue;
            targetIndex = i;
            break;
          }
        }
      }
      if (targetIndex < 0) return {};

      const msg = messagesCopy[targetIndex]!;
      const nextContent = (msg.content || "") + textChunk;
      // Finalising a placeholder that never received any content left a
      // non-streaming, empty assistant message behind, which renders as a
      // stray empty bubble. Drop it instead — but only when it carries
      // nothing else worth showing: to-do lists and widget rows are
      // legitimately empty-content messages and must survive.
      const isEmptyShell =
        done &&
        nextContent.trim().length === 0 &&
        !(msg as any).extra?.widget &&
        ((msg as any).toDos?.length ?? 0) === 0;
      if (isEmptyShell) {
        messagesCopy.splice(targetIndex, 1);
      } else {
        messagesCopy[targetIndex] = {
          ...msg,
          content: nextContent,
          isStreaming: done ? false : true,
        };
      }
      return { messages: messagesCopy };
    });
  },
  ensureThreadId: async (): Promise<string> => {
    const existing = get().currentSessionId;
    if (existing) return existing;
    const resp = await authFetch(
      `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/chats`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getWorkflowServiceContextHeaders(),
        },
      },
    );
    if (!resp.ok) {
      throw new Error(`Failed to create chat thread (${resp.status})`);
    }
    const data = (await resp.json()) as { threadId: string };
    track("chat:initiated");
    set({
      currentSessionId: data.threadId,
      redirectToThreadId: data.threadId,
      plannedWorkflow: null,
      activityRunStatus: [],
      runInputs: [],
      workflowRunStatus: "completed",
      isDesignTimeExecuting: false,
    });

    const { updateSessionsStore } = useSessionsStore.getState();
    const nowIso = new Date().toISOString();
    updateSessionsStore((prev) => {
      const exists = prev.chats.some((c) => c.id === data.threadId);
      if (exists) return {};
      return {
        chats: [
          {
            id: data.threadId,
            title: "New chat",
            description: null,
            workflowId: null,
            lastUpdatedAt: nowIso,
          },
          ...prev.chats,
        ],
      };
    });

    return data.threadId;
  },
  sendMessage: (content) => {
    const {
      addMessage,
      setInputValue,
      setInputSubmitting,
      updateLastAssistantMessage,
      ensureThreadId,
    } = get();

    // A refusal belongs to the turn it refused. Left standing it would sit under
    // the next turn's successful reply, still claiming the work was stopped.
    if (get().error) set({ error: null });

    if (get().isStreaming) {
      console.log("Agent is streaming, queueing message");
      get().enqueueMessage(content, [...get().attachments]);
      set({ attachments: [] });
      setInputValue("");
      return;
    }

    // Guard: block workflow execution if user is in Try Mode with no remaining tries
    // Conversational messages (no interrupt in progress) are still allowed
    const currentState = get();
    if (
      currentState.isTryMode &&
      currentState.remainingTries !== null &&
      currentState.remainingTries <= 0 &&
      currentState.isWaitingForInput
    ) {
      console.log("❌ Try mode limit reached — blocking execution submit");
      // Show a friendly message without sending to backend
      addMessage({ content, role: "user" });
      addMessage({
        content: `I'm sorry, but you have exhausted your free executions for this workflow in Try Mode. Please onboard me to your workspace to continue executing!\n\n<quick_replies>["Onboard me"]</quick_replies>`,
        role: "assistant",
      });
      setInputValue("");
      get().onOnboardRequest?.();
      return;
    }

    // Reset streaming state and mark any streaming assistant message as complete
    {
      set((state) => {
        const messagesCopy = [...state.messages];
        for (let i = messagesCopy.length - 1; i >= 0; i--) {
          const msg = messagesCopy[i]!;
          if (msg.role === "assistant" && msg.isStreaming) {
            messagesCopy[i] = {
              ...msg,
              isStreaming: false,
            };
            break;
          }
        }
        return {
          isStreaming: false,
          isWaitingForInput: false,
          currentInterrupt: null,
          interruptsQueue: [],
          messages: messagesCopy,
        };
      });
    }

    // If user is in design-time execution, reset state and allow new message
    const latestState = get();
    if (
      latestState.formMode === "design" &&
      latestState.isDesignTimeExecuting
    ) {
      console.log("User interrupted design-time execution with new message");
      set({
        isDesignTimeExecuting: false,
        isStreaming: false,
        formMode: null,
        isWaitingForInput: false,
        currentInterrupt: null,
        interruptsQueue: [],
      });
    }

    // Add user message
    addMessage({
      content,
      role: "user",
    });

    // Clear input (keep planned workflow until server sends an updated plan)
    setInputValue("");
    setInputSubmitting(true);

    // Add placeholder assistant message (we will update the last assistant message directly)
    addMessage({
      content: "",
      role: "assistant",
      isStreaming: true,
    });

    // Set initial thinking state and start streaming immediately
    set({
      isStreaming: true,
      currentThinking: getChatLocale() === "ja" ? "考え中…" : "Thinking...",
      currentInterrupt: null,
      interruptsQueue: [],
    });

    const DEFAULT_TITLES = new Set(["New Chat", "New chat", "new chat", ""]);

    // helper to auto-generate and update the thread title after first assistant
    // message. Skipped if the thread already has a non-default title.
    const generateAndUpdateTitle = async (threadId: string) => {
      try {
        const currentTitle = get().currentSessionTitle ?? "";
        if (currentTitle && !DEFAULT_TITLES.has(currentTitle)) return;

        // Build last 1-20 messages, excluding empty content
        const sourceMessages = get()
          .messages.filter(
            (m) =>
              (m.role === "user" || m.role === "assistant") &&
              m.content.trim().length > 0,
          )
          .slice(-20)
          .map((m) => ({
            role: m.role as "user" | "assistant",
            content: [{ type: "text", text: m.content }],
          }));

        if (sourceMessages.length === 0) return;

        const resp = await authFetch(
          `${ENV.WORKFLOW_SVC_PUBLIC_URL}/api/v1/chats/${threadId}/title`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              ...getWorkflowServiceContextHeaders(),
            },
            body: JSON.stringify({ type: "auto", messages: sourceMessages }),
          },
        );
        if (!resp.ok) return;
        const data = (await resp.json()) as { title?: string };
        const newTitle = data?.title;
        if (!newTitle) return;

        // Update chat store
        set({ currentSessionTitle: newTitle });

        // Update sessions store optimistically
        const { updateSessionsStore } = useSessionsStore.getState();
        updateSessionsStore((prev) => {
          const index = prev.chats.findIndex((c) => c.id === threadId);
          const updated = [...prev.chats];
          if (index >= 0) {
            updated[index] = {
              ...updated[index]!,
              title: newTitle,
              lastUpdatedAt: new Date().toISOString(),
            };
          }
          return { chats: updated };
        });
      } catch {
        // ignore title errors
      }
    };

    void (async () => {
      try {
        const threadId = await ensureThreadId();

        const formData = new FormData();
        formData.append("message", content);

        appendChatLocaleToFormData(formData);

        if (get().isTryMode) {
          formData.append("isTryMode", "true");
        }

        const hadAttachments = get().attachments.length > 0;
        for (const [index, a] of get().attachments.entries()) {
          formData.append(`attachments[${index}]`, a.file);
        }

        set({
          attachments: [],
        });

        // Helper to refresh contextFiles from the backend after attachments are uploaded
        const refreshContextFiles = async () => {
          if (!hadAttachments) return;
          try {
            const { contextFiles } = await getChatMessages(threadId, {
              limit: 1,
            });
            if (contextFiles && get().currentSessionId === threadId) {
              set({ contextFiles });
            }
          } catch {
            // Silently ignore — context files will show on next page load
          }
        };

        streamChat({
          threadId,
          formData,
          onMessage: (content) => updateLastAssistantMessage(content),
          onClose: () => {
            set({ isStreaming: false });
            updateLastAssistantMessage("", true);
            setInputSubmitting(false);
            void generateAndUpdateTitle(threadId);
            void refreshContextFiles();

            get().drainNextQueuedMessage();
          },
          onError: () => {
            set({ isStreaming: false });
            updateLastAssistantMessage("", true);
            setInputSubmitting(false);
            message.error(i18next.t("stream.connectionFailed", { ns: "chat" }));
            void refreshContextFiles();

            get().drainNextQueuedMessage();
          },
          onOpen: () => {
            set({ isStreaming: true });
          },
        });
      } catch (error) {
        console.error("sendMessage error", error);
        // mark not submitting on failure
        setInputSubmitting(false);
      }
    })();
  },

  resetChat: () => {
    const { resetState } = get();
    resetState();
  },

  appendWidgetMessage: (widgetData: any) => {
    set((state) => {
      const messages = [...state.messages];
      const lastMsg = messages.at(-1);

      const liveWidget = { ...widgetData, live: true };

      // If the last message is already a widget of this type, update it instead of appending a duplicate
      if (lastMsg && lastMsg.extra?.widget?.type === widgetData.type) {
        const updatedLastMsg = {
          ...lastMsg,
          extra: { ...lastMsg.extra, widget: liveWidget },
        };
        messages[messages.length - 1] = updatedLastMsg;
        return { messages };
      }

      return {
        messages: [
          ...messages,
          {
            id: `widget-${Date.now()}`,
            content: "",
            role: "assistant",
            timestamp: new Date().toISOString(),
            extra: {
              widget: liveWidget,
            },
          },
        ],
      };
    });
  },

  setPlannedWorkflow: (wf) => {
    // expand canvas panel and collapse session panel
    useGlobalStore.getState().updateSystemStatus({
      showCanvasPanel: true,
      isSessionPanelCollapsed: true,
    });
    set({ plannedWorkflow: wf });
  },

  adoptSavedMessageIds: ({ messageId, userMessageId }) => {
    set((state) => {
      const messages = [...state.messages];
      const adopt = (role: "assistant" | "user", id: string) => {
        // Widgets (attach-trigger and the like) are assistant rows too, and one
        // appended after the reply would otherwise take the reply's id.
        const index = messages
          .map((message, position) => ({ message, position }))
          .filter(
            (entry) =>
              entry.message.role === role &&
              !(entry.message as { extra?: { widget?: unknown } }).extra
                ?.widget,
          )
          .at(-1)?.position;
        if (index === undefined || messages[index]?.id === id) return;
        messages[index] = { ...messages[index], id } as ChatMessage;
      };
      adopt("assistant", messageId);
      if (userMessageId) adopt("user", userMessageId);
      return { messages };
    });
  },

  loadRestorePoints: async () => {
    const threadId = get().currentSessionId;
    if (!threadId) return;
    try {
      const points = await getChatRestorePoints(threadId);
      if (get().currentSessionId !== threadId) return;
      set({ restorePointMessageIds: points.map((point) => point.messageId) });
    } catch (error) {
      console.warn("Failed to load restore points", error);
    }
  },

  restoreChatTo: async (messageId, options) => {
    const threadId = get().currentSessionId;
    if (!threadId) return;
    await restoreChatToMessage(threadId, messageId, options ?? {});
    set((state) => ({
      queuedMessages: state.queuedMessages.filter(
        (queued) => queued.threadId !== threadId,
      ),
      error: null,
      reloadThreadId: threadId,
      threadReloadToken: state.threadReloadToken + 1,
    }));
    await get().loadRestorePoints();
  },

  setWorkflowRunStatus: (status, runId) => {
    set((state) => {
      const normalizeRunId = (
        value: string | number | null | undefined,
      ): string | null | undefined => {
        if (value === undefined) return undefined;
        if (value === null) return null;
        const asString = String(value).trim();
        return asString.length === 0 ? null : asString;
      };

      const normalizedParam = normalizeRunId(
        runId as string | number | null | undefined,
      );
      const currentRunId = state.currentWorkflowRunId;
      const isRunReset = normalizedParam === null;
      const isNewRun =
        normalizedParam !== undefined &&
        normalizedParam !== null &&
        normalizedParam !== currentRunId;

      const shouldResetActivityRuns = isRunReset || isNewRun;
      const nextActivityRunStatus = shouldResetActivityRuns
        ? []
        : state.activityRunStatus;
      const nextRunInputs = shouldResetActivityRuns ? [] : state.runInputs;

      const nextRunId =
        normalizedParam === undefined ? currentRunId : normalizedParam;

      let nextRemainingTries = state.remainingTries;
      if (
        isNewRun &&
        state.isTryMode &&
        state.remainingTries !== null &&
        state.remainingTries > 0
      ) {
        nextRemainingTries = state.remainingTries - 1;
      }

      return {
        workflowRunStatus: status,
        currentWorkflowRunId: nextRunId ?? null,
        activityRunStatus: nextActivityRunStatus,
        runInputs: nextRunInputs,
        remainingTries: nextRemainingTries,
      };
    });
  },

  setRunInputs: (inputs) => {
    set({ runInputs: inputs });
  },

  setActivityRunStatus: (items) => {
    set({ activityRunStatus: items });
  },

  setIsDesignTimeExecuting: (isExecuting) => {
    set({ isDesignTimeExecuting: isExecuting });
  },

  // Thinking display helper
  setCurrentThinking: (value) => {
    set({ currentThinking: value });
  },

  addOrUpdateTodoOnLastAssistantMessage: (
    text,
    status = "started",
    notificationId?: string,
  ) => {
    set((state) => {
      const messagesCopy = [...state.messages];
      // Only attach to streaming assistant messages (not completed ones)
      let targetIndex = -1;
      for (let i = messagesCopy.length - 1; i >= 0; i--) {
        const msg = messagesCopy[i]!;
        if (msg.role === "assistant" && msg.isStreaming) {
          targetIndex = i;
          break;
        }
      }

      if (targetIndex < 0) {
        return {};
      }

      const msg = messagesCopy[targetIndex]!;
      const existing = Array.isArray((msg as any).toDos)
        ? ([...(msg as any).toDos] as Array<{
            id: string;
            text: string;
            status: "started" | "completed" | "failed";
          }>)
        : [];

      // First try to match by notification ID if provided
      let index = notificationId
        ? existing.findIndex((t) => t.id === notificationId)
        : -1;

      // Fallback: use normalized text for matching
      if (index < 0) {
        const key = normalizeNotificationText(text).toLowerCase();
        index = existing.findIndex(
          (t) => normalizeNotificationText(t.text).toLowerCase() === key,
        );
      }

      if (index >= 0) {
        // Update existing todo: update text and status
        existing[index] = {
          ...existing[index]!,
          text: text,
          status: status as any,
        };
      } else {
        // A NEW step arriving means the previous one finished: this stream
        // reports progress sequentially and never sends a second notification to
        // close out a step it has moved past. Without this, nothing marked the
        // earlier steps done until the stream ended and
        // `completeTodosOnLastAssistantMessage` swept the whole list — so the
        // card was all-or-nothing: every row spinning, or every row ticked
        // (including the one still waiting on the user). Now each row settles as
        // the next one starts, leaving exactly one spinner: the current step.
        //
        // The timer pseudo-to-do is exempt — it stays `started` for the life of
        // the stream because the header reads it as the elapsed-time badge.
        for (let i = 0; i < existing.length; i++) {
          const todo = existing[i]!;
          if (todo.status === "started" && todo.id !== STREAM_TIMER_TODO_ID) {
            existing[i] = { ...todo, status: "completed" };
          }
        }

        // Add new todo with full original text and notification ID
        existing.push({
          id: notificationId || crypto.randomUUID(),
          text: text,
          status: status as any,
        });
      }
      (messagesCopy[targetIndex] as any) = { ...msg, toDos: existing };

      return { messages: messagesCopy };
    });
  },

  completeTodosOnLastAssistantMessage: (isSuccess) => {
    set((state) => {
      const messagesCopy = [...state.messages];
      // The newest assistant turn that actually CARRIES to-dos, not simply the
      // newest assistant message. A run-status notification is an assistant row
      // too, so the old backwards walk could stop on one of those, write an empty
      // `toDos` onto it, and leave the real to-dos spinning forever.
      for (let i = messagesCopy.length - 1; i >= 0; i--) {
        const msg = messagesCopy[i]!;
        if (msg.role !== "assistant") continue;
        if ((msg as any).extra?.isNotification) continue;

        const existing = (msg as any).toDos as
          | Array<{
              id: string;
              text: string;
              status: "started" | "completed" | "failed";
            }>
          | undefined;
        if (!Array.isArray(existing) || existing.length === 0) continue;

        // Nothing in flight: return an empty patch so this does not churn a new
        // message array (and a re-render) on every terminal stream event.
        if (!existing.some((todo) => todo.status === "started")) return {};

        const nextStatus = isSuccess ? "completed" : "failed";
        (messagesCopy[i] as any) = {
          ...msg,
          toDos: existing.map((todo) =>
            todo.status === "started" ? { ...todo, status: nextStatus } : todo,
          ),
        };
        return { messages: messagesCopy };
      }
      return {};
    });
  },

  // Token tracking methods
  updateTokenCount: (inputTokens, outputTokens, model = "gpt-4") => {
    const totalTokens = inputTokens + outputTokens;
    set((state) => ({
      sessionTokenCount: state.sessionTokenCount + totalTokens,
      lastMessageTokens: {
        inputTokens,
        outputTokens,
        totalTokens,
        model,
      },
    }));
  },

  resetSessionTokens: () => {
    set({
      sessionTokenCount: 0,
      lastMessageTokens: null,
    });
  },

  setMonthlyTokenCount: (count) => {
    set({ monthlyTokenCount: count });
  },

  setLastStreamEvent: (event) => {
    set({
      lastStreamEvent: {
        ...event,
        at: Date.now(),
      },
    });
  },

  setOpenUnconnectedIntegrationsModal: (payload) => {
    set({ openUnconnectedIntegrationsModal: payload });
  },

  initializeTokenStats: async () => {
    const { setMonthlyTokenCount } = get();
    await loadUserTokenStats(setMonthlyTokenCount);
  },

  stopStream: () => {
    // Abort the chat SSE for the CURRENT session using the Map lookup.
    const currentSessionId = get().currentSessionId;
    const sseMap = (window as any).sseControllers as
      | Map<string, { controller: AbortController; threadId: string }>
      | undefined;
    if (currentSessionId && sseMap?.has(currentSessionId)) {
      const entry = sseMap.get(currentSessionId)!;
      console.log("Aborting SSE stream for session:", currentSessionId);
      entry.controller.abort();
      sseMap.delete(currentSessionId);
      get().removeRunningSessionId(currentSessionId);
      // Also clear the legacy reference if it matches
      const legacySSE = (window as any).currentSSEController;
      if (legacySSE?.threadId === currentSessionId) {
        (window as any).currentSSEController = null;
      }
    }
    // Also abort the workflow-run SSE subscription (from subscribeToWorkflowRun)
    if ((window as any).currentWorkflowRunController) {
      console.log("Aborting current workflow run SSE stream manually");
      try {
        (window as any).currentWorkflowRunController.controller.abort();
      } catch (err) {
        console.error(
          "Error aborting workflow run SSE controller during stop:",
          err,
        );
      }
      (window as any).currentWorkflowRunController = null;
    }

    const {
      isDesignTimeExecuting,
      currentWorkflowRunId,
      workflowRunStatus: originalStatus,
    } = get();
    console.log("[stopStream] state:", {
      currentWorkflowRunId,
      workflowRunStatus: originalStatus,
      isDesignTimeExecuting,
    });

    // Pause this thread's queue rather than letting it fire later.
    //
    // The queue is only ever drained from streamChat's onClose/onError, and
    // neither of those runs on an explicit abort (see chat-sse.ts). Left armed,
    // an abandoned message gets sent by the *next* unrelated stream's onClose —
    // a surprise send minutes after the user stopped. Keep the chips (stopping
    // is not a retraction of what they typed) and let the user decide via the
    // Send now / Discard actions the paused state reveals.
    set((state) => ({
      queuedMessages: state.queuedMessages.map((q) =>
        q.threadId === currentSessionId ? { ...q, paused: true } : q,
      ),
    }));

    set((state) => {
      const messagesCopy = [...state.messages];
      for (let i = messagesCopy.length - 1; i >= 0; i--) {
        const msg = messagesCopy[i]!;
        if (msg.role === "assistant" && msg.isStreaming) {
          // Any to-do still `started` is finalised in the same pass. The user
          // aborted, so it never completed -- and `failed` is the only terminal
          // status the shape has. Leaving them `started` would spin forever,
          // since nothing else will ever report on this turn again.
          const toDos = (msg as any).toDos as
            | Array<{
                id: string;
                text: string;
                status: "started" | "completed" | "failed";
              }>
            | undefined;
          messagesCopy[i] = {
            ...msg,
            isStreaming: false,
            ...(Array.isArray(toDos)
              ? {
                  toDos: toDos.map((todo) =>
                    todo.status === "started"
                      ? { ...todo, status: "failed" as const }
                      : todo,
                  ),
                }
              : {}),
          } as typeof msg;
          break;
        }
      }

      const updatedActivityRunStatus = state.activityRunStatus.map((a) =>
        a.status === "running" || a.status === "waiting"
          ? { ...a, status: "cancelled" as const }
          : a,
      );

      return {
        isStreaming: false,
        isDesignTimeExecuting: false,
        // Only claim a cancelled RUN when one was actually in flight. Stopping
        // during the planning phase has no run behind it, and unconditionally
        // writing "cancelled" made the canvas report a run that never ran.
        //
        // Keyed off the STATUS as well as the id, not the id alone:
        // setWorkflowRunStatus("running", undefined) leaves currentWorkflowRunId
        // null (see its normalizeRunId branch), which happens whenever a
        // workflow-run frame carries no parseable run id. Gating on the id alone
        // would leave such a run pinned at "running" forever after Stop.
        ...(currentWorkflowRunId ||
        originalStatus === "running" ||
        originalStatus === "waiting"
          ? { workflowRunStatus: "cancelled" as const }
          : {}),
        activityRunStatus: updatedActivityRunStatus,
        currentThinking: null,
        messages: messagesCopy,
      };
    });

    // Cancel the Temporal workflow if there's a running run — regardless of
    // whether we're in "design time" or the activity-execution phase.
    // Also handle null status (e.g. session switch in progress).
    if (
      currentWorkflowRunId &&
      originalStatus !== "completed" &&
      originalStatus !== "failed" &&
      originalStatus !== "cancelled"
    ) {
      console.log("[stopStream] Canceling workflow run:", currentWorkflowRunId);
      cancelWorkflowRuns({ runIds: [currentWorkflowRunId] }).catch((err) => {
        console.error("[stopStream] Failed to cancel workflow run:", err);
        message.error("Failed to cancel workflow: " + err.message);
        set({ workflowRunStatus: originalStatus }); // Revert on failure
      });
    } else {
      console.log("[stopStream] No active workflow run to cancel");
    }
  },

  removeContextFile: (fileId: string) => {
    const threadId = get().currentSessionId;
    if (!threadId) return;

    // Optimistic removal from local state
    const previousFiles = get().contextFiles;
    set({
      contextFiles: previousFiles.filter((f) => f.id !== fileId),
    });

    // Call backend to soft-delete
    deleteContextFile(threadId, fileId).catch((err) => {
      console.error("Failed to delete context file:", err);
      message.error(i18next.t("attachments.removeFailed", { ns: "chat" }));
      // Rollback on failure
      set({ contextFiles: previousFiles });
    });
  },
}));

export type {
  ChatState,
  ChatStore,
  ChatMessage,
  QueuedMessage,
  ChatInputState,
  Attachment,
  StreamEvent,
};
